import React, { lazy } from 'react';
import { CheckCircleIcon, UsersIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { SectionHeading } from '../components/SectionHeading';
import { PageHero } from '../components/PageHero';
import { CTASection } from '../components/CTASection';
interface OverOnsPageProps {
  onNavigate: (page: string) => void;
}
export function OverOnsPage({
  onNavigate
}: OverOnsPageProps) {
  return <div className="w-full" data-id="element-668">
      {/* Hero */}
      <PageHero title="Jouw persoonlijke adviseur" subtitle="Met meer dan 15 jaar ervaring in de financiële dienstverlening. Persoonlijk, snel en zonder gedoe." label="Over Jordy" data-id="element-669" />

      {/* Editorial Layout - Part 1 */}
      <section className="py-20 md:py-28 bg-white" data-id="element-670">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-671">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-672">
            <div className="w-full lg:w-1/2 order-2 lg:order-1" data-id="element-673">
              <FadeIn data-id="element-674">
                <div className="space-y-6 text-lg text-gray-600 leading-relaxed" data-id="element-675">
                  <p data-id="element-676">
                    Hoi, ik ben Jordy de Gier. Met meer dan 15 jaar ervaring in
                    de financiële dienstverlening heb ik JDG Hypotheken |
                    Verzekeringen opgericht vanuit één duidelijke visie:
                    financieel advies moet persoonlijker, sneller en zonder
                    gedoe.
                  </p>
                  <p data-id="element-677">
                    Ik geloof niet in corporate onzin, ingewikkelde keuzemenu's
                    of wisselende contactpersonen. Financiële keuzes zijn vaak
                    de belangrijkste beslissingen in je leven, en die maak je
                    het best met iemand die je kent en vertrouwt.
                  </p>
                  <p data-id="element-678">
                    Waarom ik dit doe? Omdat ik het verschil wil maken op het
                    moment dat het ertoe doet. Als een bank 'nee' zegt tegen een
                    starter, als een ondernemer vastloopt, of als iemand na een
                    scheiding niet weet of ze in het huis kan blijven wonen. Dán
                    wil ik degene zijn die wél een oplossing vindt.
                  </p>
                </div>
              </FadeIn>
            </div>

            <div className="w-full lg:w-1/2 order-1 lg:order-2 relative" data-id="element-679">
              <FadeIn delay={0.2} className="relative z-10" data-id="element-680">
                <img src="https://cdn.magicpatterns.com/uploads/8YqgEQJ5jSY63NDsDuuJzL/5A2A7661-bewerkt-3.jpg" alt="Jordy de Gier" loading="lazy" className="w-full max-w-md mx-auto rounded-2xl shadow-card-lg object-cover aspect-[3/4]" data-id="element-681" />
              </FadeIn>
              <div className="absolute top-10 -right-10 w-full h-full bg-warm rounded-2xl -z-10 hidden md:block" data-id="element-682" />
            </div>
          </div>
        </div>
      </section>

      {/* Ons Team Section */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-683">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-684">
          <SectionHeading title="Ons Team" subtitle="Klein genoeg voor persoonlijk contact, groot genoeg om altijd bereikbaar te zijn." data-id="element-685" />

          <div className="max-w-3xl mx-auto text-center" data-id="element-686">
            <FadeIn delay={0.2} className="bg-white p-8 md:p-10 rounded-2xl shadow-card border border-gray-100" data-id="element-687">
              <div className="w-14 h-14 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-688">
                <UsersIcon className="w-7 h-7 text-gold" data-id="element-689" />
              </div>
              <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-690">
                JDG Hypotheken | Verzekeringen bestaat momenteel uit een hecht
                team van 3 professionals. We werken nauw samen, waardoor we
                altijd op de hoogte zijn van jouw dossier.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-691">
                Hoewel we ambities hebben om te groeien, zal dit nooit ten koste
                gaan van het persoonlijke contact. Je spreekt altijd direct met
                iemand die jou en jouw situatie kent.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Erkend Financieel Adviseur Badge Section */}
      <section className="py-20 md:py-28 bg-navy text-white" data-id="element-692">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-693">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-694">
            <FadeIn className="w-full md:w-1/3 flex justify-center" data-id="element-695">
              <div className="bg-white p-6 rounded-2xl shadow-xl" data-id="element-696">
                <img src="https://cdn.magicpatterns.com/uploads/hLrKue75gCT8qjkamHbfdK/JDG_Erkend_adviseur.png" alt="Erkend Financieel Adviseur" className="w-48 h-auto object-contain" data-id="element-697" />
              </div>
            </FadeIn>
            <div className="w-full md:w-2/3" data-id="element-698">
              <FadeIn delay={0.2} data-id="element-699">
                <h2 className="text-3xl font-bold text-gold mb-6" data-id="element-700">
                  Erkend Financieel Adviseur
                </h2>
                <p className="text-lg text-gray-300 leading-relaxed mb-8" data-id="element-701">
                  Ik voldoe aan de hoogste kwaliteitseisen in de branche.
                  Doorlopend bijgeschoold en getoetst. Zo ben jij altijd
                  verzekerd van actueel, onafhankelijk en betrouwbaar advies.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-702">
                  <div className="flex items-center gap-3" data-id="element-703">
                    <CheckCircleIcon className="w-6 h-6 text-gold flex-shrink-0" data-id="element-704" />
                    <span className="font-medium" data-id="element-705">
                      Wft-vergunning Hypothecair Krediet
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-706">
                    <CheckCircleIcon className="w-6 h-6 text-gold flex-shrink-0" data-id="element-707" />
                    <span className="font-medium" data-id="element-708">
                      Wft-vergunning Schadeverzekeringen
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-709">
                    <CheckCircleIcon className="w-6 h-6 text-gold flex-shrink-0" data-id="element-710" />
                    <span className="font-medium" data-id="element-711">
                      Wft-vergunning Levensverzekeringen
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-712">
                    <CheckCircleIcon className="w-6 h-6 text-gold flex-shrink-0" data-id="element-713" />
                    <span className="font-medium" data-id="element-714">
                      Erkend Financieel Adviseur
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-715">
                    <CheckCircleIcon className="w-6 h-6 text-gold flex-shrink-0" data-id="element-716" />
                    <span className="font-medium" data-id="element-717">Aangesloten bij Kifid</span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-718">
                    <CheckCircleIcon className="w-6 h-6 text-gold flex-shrink-0" data-id="element-719" />
                    <span className="font-medium" data-id="element-720">
                      Doorlopende opleiding en bijscholing
                    </span>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Layout - Part 2 */}
      <section className="py-20 md:py-28 bg-white" data-id="element-721">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-722">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-723">
            <div className="w-full lg:w-1/2 relative" data-id="element-724">
              <FadeIn className="relative z-10" data-id="element-725">
                <img src="https://cdn.magicpatterns.com/uploads/xuQDP4soQA1T2E7yMeP3Fj/5A2A7557-bewerkt-3.jpg" alt="Jordy de Gier in kantoor" loading="lazy" className="w-full max-w-md mx-auto rounded-2xl shadow-card-lg object-cover aspect-square" data-id="element-726" />
              </FadeIn>
              <div className="absolute -bottom-10 -left-10 w-full h-full bg-navy/5 rounded-2xl -z-10 hidden md:block" data-id="element-727" />
            </div>

            <div className="w-full lg:w-1/2" data-id="element-728">
              <FadeIn delay={0.2} data-id="element-729">
                <h2 className="text-3xl font-bold text-navy mb-6" data-id="element-730">
                  Lokaal geworteld, landelijk actief
                </h2>
                <div className="space-y-6 text-lg text-gray-600 leading-relaxed" data-id="element-731">
                  <p data-id="element-732">
                    Hoewel ik klanten door het hele land help, ligt mijn hart in
                    de regio Badhoevedorp, Amsterdam en Hoofddorp, de
                    Haarlemmermeer en de Schiphol-regio. Ik ken de lokale markt
                    als geen ander.
                  </p>
                  <p data-id="element-733">
                    Of je nu starter bent, ondernemer, of in een scheiding ligt:
                    ik neem de tijd voor je. Geen snelle verkooppraatjes, maar
                    een duurzame relatie. Veel van mijn klanten bellen me jaren
                    later nog steeds voor advies over pensioen,
                    belastingaangifte of andere financiële vraagstukken. Dat is
                    voor mij het ultieme bewijs van vertrouwen.
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Office Location Section */}
      <section className="py-20 md:py-28 bg-warm" data-id="element-734">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center" data-id="element-735">
          <FadeIn data-id="element-736">
            <h2 className="text-3xl font-bold text-navy mb-12" data-id="element-737">
              Kom gerust langs
            </h2>
            <div className="relative max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-card-lg" data-id="element-738">
              <img src="https://cdn.magicpatterns.com/uploads/kirqiWzVxRbHNevkp7sjEZ/JDG_Nieuwsbrief_heading_goed_zonder_(1).jpg" alt="JDG Kantoor Badhoevedorp" loading="lazy" className="w-full h-auto object-cover" data-id="element-739" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent flex items-end justify-center pb-10" data-id="element-740">
                <div className="text-white" data-id="element-741">
                  <p className="text-2xl font-bold mb-2" data-id="element-742">Sloterweg 228</p>
                  <p className="text-lg opacity-90" data-id="element-743">Badhoevedorp</p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection title="Benieuwd wat ik voor je kan betekenen?" subtitle="Plan een vrijblijvende kennismaking. Ik neem de tijd om jouw situatie te begrijpen." buttonText="Plan een gratis kennismaking" onNavigate={onNavigate} page="contact" variant="light" data-id="element-744" />
    </div>;
}